import React, { useEffect, useMemo, useState } from 'react';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Chip,
  Button,
  CircularProgress,
  Divider,
  Tooltip
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import StorageIcon from '@material-ui/icons/Storage';
import StatsCard from '../shared/StatsCard';
import LiveIndicator from '../shared/LiveIndicator';
import MetricsChart from './MetricsChart';
import XtermLogViewer from '../shared/XtermLogViewer';
import { useAdminWebSocketStore } from '../../../stores/websocketStore';
import utils from "../../../universal/utils";
import {
  formatIdleDiagnosticsTime,
  getIdleDiagnosticsPresentation,
  pickIdleDiagnostics
} from './idleDiagnosticsView';

const useStyles = makeStyles((theme) => ({
  root: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4)
  },
  header: {
    marginBottom: theme.spacing(4),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2)
  },
  headerStatusRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
    marginTop: theme.spacing(1),
  },
  paper: {
    padding: theme.spacing(3)
  },
  section: {
    marginTop: theme.spacing(3)
  },
  infoRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: theme.spacing(1.5),
    '&:nth-of-type(odd)': {
      backgroundColor: theme.palette.action.hover
    }
  },
  infoLabel: {
    fontWeight: 500,
    color: theme.palette.text.secondary
  },
  infoValue: {
    fontFamily: 'monospace'
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 400
  },
  journalControls: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    flexWrap: 'wrap'
  },
  journalFilters: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexWrap: 'wrap'
  }
}));

const AgentDetails = ({ 
  agentData,
  loading,
  onBack,
  userId,
  idleDiagnostics: idleDiagnosticsFromOverview
}) => {
  const classes = useStyles();
  const { subscribe } = useAdminWebSocketStore();
  const [journalEntries, setJournalEntries] = useState([]);
  const [journalFilter, setJournalFilter] = useState('all');
  const [followJournal, setFollowJournal] = useState(true);
  const basePath = useMemo(() => utils.getEdgeBasePath(), []);

  // IMPORTANT: Hooks must run unconditionally (no early returns before effects).
  const agentIp = agentData?.agentIp;
  const agentExists = Boolean(agentData?.exists);
  
  // Initial fetch of recent journal entries
  useEffect(() => {
    if (!agentExists) return;
    if (!agentIp || !userId) return;

    const controller = new AbortController();

    async function loadJournal() {
      try {
        const res = await fetch(
          `${basePath}/api/v7/admin/agent/${agentIp}/journal/adpass/${userId}?limit=200`,
          { signal: controller.signal }
        );
        const data = await res.json();
        if (data?.ok && Array.isArray(data.entries)) {
          setJournalEntries(data.entries);
        }
      } catch (e) {
        // best-effort
      }
    }

    loadJournal();
    return () => {
      controller.abort();
    };
  }, [agentIp, userId, basePath, agentExists]);

  // Live stream of journal batches over Socket.IO
  useEffect(() => {
    if (!agentExists) return;
    if (!agentIp) return;

    const unsubscribe = subscribe(({ message }) => {
      if (!message) return;
      if (message.messageId !== 'agent-journal-batch') return;
      if (message.agentIp !== agentIp) return;
      if (!Array.isArray(message.entries) || message.entries.length === 0) return;

      setJournalEntries((prev) => {
        const merged = [...message.entries, ...prev];
        return merged.slice(0, 500);
      });
    });

    return unsubscribe;
  }, [subscribe, agentIp, agentExists]);

  const journalFilters = useMemo(() => {
    function safeStr(v) {
      return (v || '').toString();
    }

    function entryType(e) {
      return safeStr(e?.type || e?.messageId || 'event');
    }

    function isWarmupLog(e) {
      return safeStr(e?.filePath).includes('warmup.log');
    }

    function isLog(e) {
      return Boolean(e?.filePath);
    }

    function isCommand(e) {
      const t = entryType(e);
      return Boolean(e?.command) || t.includes('host-exec');
    }

    function isOutput(e) {
      return Boolean(e?.stdout || e?.stderr || e?.output || e?.error);
    }

    return [
      { id: 'all', label: 'All', predicate: () => true },
      { id: 'warmup', label: 'warmup.log', predicate: (e) => isWarmupLog(e) },
      { id: 'logs', label: 'Logs', predicate: (e) => isLog(e) },
      { id: 'commands', label: 'Commands', predicate: (e) => isCommand(e) },
      { id: 'outputs', label: 'Outputs', predicate: (e) => isOutput(e) }
    ];
  }, []);

  const visibleJournalEntries = useMemo(() => {
    const safe = Array.isArray(journalEntries) ? journalEntries : [];
    const active =
      journalFilters.find((f) => f.id === journalFilter) ||
      journalFilters.find((f) => f.id === 'all');

    return safe
      .slice()
      .sort((a, b) => (a.ts || 0) - (b.ts || 0)) // oldest -> newest (so "follow bottom" matches newest)
      .filter((e) => active?.predicate?.(e));
  }, [journalEntries, journalFilter, journalFilters]);

  function formatJournalEntry(e) {
    const ts = e.ts ? new Date(e.ts).toISOString() : 'unknown-ts';
    const type = e.type || e.messageId || 'event';
    const cmd = e.command ? ` cmd=${e.command}` : '';
    const file = e.filePath ? ` file=${e.filePath}` : '';
    const match = e.matchId ? ` match=${e.matchId}` : '';
    const line1 = `[${ts}] ${type}${match}${file}${cmd}`;
    const payload =
      e.chunk || e.stdout || e.stderr || e.output || e.error
        ? `\n${(e.chunk || e.stdout || e.stderr || e.output || e.error || '').toString()}`
        : '';
    return `${line1}${payload}`;
  }

  const terminalContent = useMemo(() => {
    if (visibleJournalEntries.length === 0) return 'No journal entries yet.';
    return visibleJournalEntries.map(formatJournalEntry).join('\n\n');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleJournalEntries]);

  if (loading || !agentData) {
    return (
      <Container className={classes.root}>
        <Box className={classes.loadingContainer}>
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }
  
  if (!agentData.exists) {
    return (
      <Container className={classes.root}>
        <Paper className={classes.paper}>
          <Typography variant="h6">Agent Not Found</Typography>
          <Box mt={2}>
            <Button startIcon={<ArrowBackIcon />} onClick={onBack}>
              Back to Dashboard
            </Button>
          </Box>
        </Paper>
      </Container>
    );
  }
  
  const { current, history } = agentData;
  const isOnline = current.lastSeen && (Date.now() - current.lastSeen) < 60000;
  const idleDiagnostics = pickIdleDiagnostics(
    current.idleDiagnostics,
    idleDiagnosticsFromOverview
  );
  const diagnosticsPresentation = getIdleDiagnosticsPresentation(idleDiagnostics);
  
  const getHealthColor = (health) => {
    if (health === 'healthy') return 'success';
    if (health === 'degraded') return 'warning';
    return 'error';
  };
  
  return (
    <Container className={classes.root} maxWidth="xl">
      {/* Header */}
      <Box className={classes.header}>
        <Box className={classes.headerLeft}>
          <Button 
            startIcon={<ArrowBackIcon />} 
            onClick={onBack}
            variant="outlined"
          >
            Back
          </Button>
          <StorageIcon color="primary" style={{ fontSize: 40 }} />
          <Box>
            <Typography variant="h4">
              Agent {agentData.agentIp}
            </Typography>
            <Box className={classes.headerStatusRow}>
              <LiveIndicator live={isOnline} />
              <Chip 
                label={current.state} 
                color="primary"
                size="small"
              />
              <Chip 
                label={current.health?.overall || 'unknown'}
                color={getHealthColor(current.health?.overall)}
                size="small"
              />
              <Tooltip title={diagnosticsPresentation.tooltip} placement="top">
                <Chip
                  label={`Plugins ${diagnosticsPresentation.label}`}
                  color={diagnosticsPresentation.color}
                  size="small"
                />
              </Tooltip>
            </Box>
          </Box>
        </Box>
      </Box>
      
      {/* Current Stats Cards */}
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="CPU Usage"
            value={`${current.cpu?.usage?.toFixed(1) || 0}%`}
            subtitle={`${current.cpu?.cores || 0} cores`}
            color={current.cpu?.usage > 80 ? 'error' : 'primary'}
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Memory"
            value={`${current.memory?.percentUsed?.toFixed(1) || 0}%`}
            subtitle={`${Math.floor((current.memory?.used || 0) / 1024 / 1024 / 1024)}GB used`}
            color={current.memory?.percentUsed > 80 ? 'error' : 'primary'}
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Disk Usage"
            value={`${current.disk?.percentUsed?.toFixed(1) || 0}%`}
            subtitle={`${Math.floor((current.disk?.free || 0) / 1024 / 1024 / 1024)}GB free`}
            color={current.disk?.percentUsed > 80 ? 'error' : 'primary'}
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="CS2 Server"
            value={current.cs2?.running ? 'Running' : 'Stopped'}
            subtitle={current.cs2?.pid ? `PID: ${current.cs2.pid}` : 'Not active'}
            color={current.cs2?.running ? 'success' : 'default'}
          />
        </Grid>
      </Grid>
      
      {/* Historical Charts */}
      <Box className={classes.section}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <MetricsChart
              title="CPU Usage (Last Hour)"
              data={history.cpu}
              label="CPU"
              color="#1976d2"
              yAxisLabel="%"
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <MetricsChart
              title="Memory Usage (Last Hour)"
              data={history.memory}
              label="Memory"
              color="#9c27b0"
              yAxisLabel="%"
            />
          </Grid>
        </Grid>
      </Box>
      
      {/* System Information */}
      <Box className={classes.section}>
        <Paper className={classes.paper}>
          <Typography variant="h6" gutterBottom>
            System Information
          </Typography>
          
          <Box className={classes.infoRow}>
            <Typography className={classes.infoLabel}>Hostname</Typography>
            <Typography className={classes.infoValue}>
              {current.hostname || agentData.agentHostname}
            </Typography>
          </Box>
          
          <Box className={classes.infoRow}>
            <Typography className={classes.infoLabel}>Instance ID</Typography>
            <Typography className={classes.infoValue}>
              {agentData.agentInstanceId || 'N/A'}
            </Typography>
          </Box>

          <Box className={classes.infoRow}>
            <Typography className={classes.infoLabel}>AWS Region</Typography>
            <Typography className={classes.infoValue}>
              {current.awsRegion || agentData.awsRegion || 'N/A'}
            </Typography>
          </Box>
          
          <Box className={classes.infoRow}>
            <Typography className={classes.infoLabel}>System Uptime</Typography>
            <Typography className={classes.infoValue}>
              {current.uptime?.system 
                ? `${Math.floor(current.uptime.system / 3600)}h`
                : 'N/A'
              }
            </Typography>
          </Box>
          
          <Box className={classes.infoRow}>
            <Typography className={classes.infoLabel}>Process Uptime</Typography>
            <Typography className={classes.infoValue}>
              {current.uptime?.process 
                ? `${Math.floor(current.uptime.process / 3600)}h`
                : 'N/A'
              }
            </Typography>
          </Box>
          
          <Box className={classes.infoRow}>
            <Typography className={classes.infoLabel}>Active Connections</Typography>
            <Typography className={classes.infoValue}>
              {current.network?.connections || 0}
            </Typography>
          </Box>
          
          {current.matchId && (
            <Box className={classes.infoRow}>
              <Typography className={classes.infoLabel}>Current Match</Typography>
              <Typography className={classes.infoValue}>
                {current.matchId}
              </Typography>
            </Box>
          )}
        </Paper>
      </Box>

      <Box className={classes.section}>
        <Paper className={classes.paper}>
          <Typography variant="h6" gutterBottom>
            Plugin diagnostics
          </Typography>

          <Box className={classes.infoRow}>
            <Typography className={classes.infoLabel}>Idle RCON check</Typography>
            <Chip
              label={diagnosticsPresentation.label}
              color={diagnosticsPresentation.color}
              size="small"
            />
          </Box>

          <Box className={classes.infoRow}>
            <Typography className={classes.infoLabel}>Last checked</Typography>
            <Typography className={classes.infoValue}>
              {formatIdleDiagnosticsTime(idleDiagnostics?.checkedAt) || 'Not checked yet'}
            </Typography>
          </Box>

          <Box className={classes.infoRow}>
            <Typography className={classes.infoLabel}>MetaMod</Typography>
            <Typography className={classes.infoValue}>
              {idleDiagnostics?.metamodVersion || 'N/A'}
            </Typography>
          </Box>

          <Box className={classes.infoRow}>
            <Typography className={classes.infoLabel}>CounterStrikeSharp</Typography>
            <Typography className={classes.infoValue}>
              {idleDiagnostics?.counterStrikeSharpVersion || 'N/A'}
            </Typography>
          </Box>

          {(idleDiagnostics?.cssPlugins || []).map((plugin) => (
            <Box className={classes.infoRow} key={`${plugin.name}-${plugin.status}`}>
              <Typography className={classes.infoLabel}>{plugin.name || 'Plugin'}</Typography>
              <Typography className={classes.infoValue}>
                {[plugin.status, plugin.version].filter(Boolean).join(' · ') || 'N/A'}
              </Typography>
            </Box>
          ))}

          {(idleDiagnostics?.issues || []).map((issue) => (
            <Box className={classes.infoRow} key={issue}>
              <Typography className={classes.infoLabel}>Issue</Typography>
              <Typography className={classes.infoValue}>{issue}</Typography>
            </Box>
          ))}
        </Paper>
      </Box>
      
      {/* CS2 Server Details */}
      {current.cs2?.running && (
        <Box className={classes.section}>
          <Paper className={classes.paper}>
            <Typography variant="h6" gutterBottom>
              CS2 Server Details
            </Typography>
            
            <Box className={classes.infoRow}>
              <Typography className={classes.infoLabel}>Process ID</Typography>
              <Typography className={classes.infoValue}>{current.cs2.pid}</Typography>
            </Box>
            
            <Box className={classes.infoRow}>
              <Typography className={classes.infoLabel}>CPU Usage</Typography>
              <Typography className={classes.infoValue}>
                {current.cs2.cpu?.toFixed(1)}%
              </Typography>
            </Box>
            
            <Box className={classes.infoRow}>
              <Typography className={classes.infoLabel}>Memory Usage</Typography>
              <Typography className={classes.infoValue}>
                {current.cs2.memory?.toFixed(1)}%
              </Typography>
            </Box>
            
            <Box className={classes.infoRow}>
              <Typography className={classes.infoLabel}>Uptime</Typography>
              <Typography className={classes.infoValue}>
                {current.cs2.uptime || 'N/A'}
              </Typography>
            </Box>
            
            <Box className={classes.infoRow}>
              <Typography className={classes.infoLabel}>Port</Typography>
              <Typography className={classes.infoValue}>
                {current.cs2.port || 27015}
              </Typography>
            </Box>
          </Paper>
        </Box>
      )}

      {/* Agent Journal */}
      <Box className={classes.section}>
        <Paper className={classes.paper}>
          <Typography variant="h6" gutterBottom>
            Agent Journal (Live)
          </Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Latest events received from the agent (commands + outputs + tailed log chunks).
          </Typography>
          <Box className={classes.journalControls}>
            <Box className={classes.journalFilters}>
              {journalFilters.map((f) => (
                <Chip
                  key={f.id}
                  label={f.label}
                  clickable
                  size="small"
                  color={journalFilter === f.id ? 'primary' : 'default'}
                  onClick={() => setJournalFilter(f.id)}
                />
              ))}
            </Box>
            <Button
              size="small"
              variant={followJournal ? 'contained' : 'outlined'}
              color="primary"
              onClick={() => setFollowJournal((v) => !v)}
            >
              Follow: {followJournal ? 'On' : 'Off'}
            </Button>
          </Box>
          <Divider style={{ margin: '12px 0' }} />
          <XtermLogViewer content={terminalContent} follow={followJournal} height={620} fontSize={14} />
        </Paper>
      </Box>
    </Container>
  );
};

export default AgentDetails;

